import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import clsx from 'clsx';
import Glide from '@glidejs/glide';
import { HotelCarouselItem, SignatureCarouselProps } from './SignatureCarousel.types';
import { StyledSignatureCarousel } from './SignatureCarousel.styles';
import { Image } from '@marriott/mi-ui-library';
import { getDirection, shuffleCards, useDCADynamicModelAlloy } from '../../modules/helpers';

const SignatureCarouselComp: FC<SignatureCarouselProps> = props => {
  /**
   * Fetching the target response
   */
  const mboxParam = props?.model?.mboxParameter;

  /***
   * Makes the call to target if mboxParam is authored
   */
  const targetResponse = useDCADynamicModelAlloy(mboxParam);
  const targetData = targetResponse?.targetData;

  /**
   * Replacing the AEM model data with target data if target Response is present
   */
  const model: HotelCarouselItem[] = targetData ? targetData : props?.model?.hotelCarousel;

  const regex = new RegExp('color-scheme2|color-scheme4|color-scheme7');
  /**
   * Reference for the Glide carousel instance
   */
  const glideRef = useRef(null);

  /**
   * Create an array to hold references for each slide
   */
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  const previousButton = useRef<HTMLButtonElement | null>(null);
  const nextButton = useRef<HTMLButtonElement | null>(null);

  //Shuffling the cards
  const [shuffledModel, setShuffledModel] = useState(model);

  /**
   * return's the direction ('rtl' / 'ltr')...
   */
  const dir = getDirection();

  // State to track the currently active slide
  const [activeIndex, setActiveIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState('');

  //set's the direction rtl/ltr
  const [direction, setDirection] = useState(false);

  const calculatePeek = () => {
    // No peek on mobile
    if (window.innerWidth <= 767) {
      return { before: 0, after: 42 };
    }
    // Tablet
    if (window.innerWidth >= 768 && window.innerWidth <= 991) {
      return { before: (window.innerWidth - 710) / 2, after: (window.innerWidth - 710) / 2 };
    }
    // Desktop
    return { before: (window.innerWidth - 992) / 2, after: (window.innerWidth - 992) / 2 };
  };

  useEffect(() => {
    /**
     * shuffling the cards randomly
     */
    const shuffledCards = props?.model?.isRandom && shuffleCards(model);

    /**
     * setting the shuffled card's based on the isRandom flag
     */
    props?.model?.isRandom && shuffledCards && setShuffledModel(shuffledCards);
  }, [model]);

  useLayoutEffect(() => {
    /**
     * setting the direction rtl/ltr
     */
    setDirection(dir);
  }, []);

  // Function to calculate gap between slides
  const calculateGap = (): number => {
    return window.innerWidth <= 767 ? -16 : 8;
  };

  useEffect(() => {
    if (!glideRef.current) return; // Ensure glideRef is not null

    // Create and mount the Glide slider instance
    const glideInst = new Glide(glideRef.current, {
      type: 'slider',
      perView: 1,
      peek: calculatePeek(), // Set initial peek values
      gap: calculateGap(), // Set initial gap value
      animationTimingFunc: 'ease-in-out',
      rewind: false,
      direction: direction ? 'rtl' : 'ltr',
      startAt: 0,
      perTouch: 1,
      animationDuration: 1200,
      breakpoints: {
        [window.innerWidth]: {
          peek: calculatePeek(), // Update peek values on resize
          gap: calculateGap(), // Update gap value on resize
        },
      },
    });

    const glideInstance = glideInst.mount({ ArrowDisabler });

    glideInst.on('run', event => {
      const direction = event.direction === '>' ? 'right' : 'left';

      //set Active Index
      setActiveIndex(glideInstance.index);

      /**
       * Set the Next and Previous based on the Direction of the slide changes
       */
      if (glideInstance.index <= shuffledModel.length - 1 && direction === 'right') {
        setNextIndex('Next');
      }
      if (glideInstance.index >= 0 && direction === 'left') {
        setNextIndex('Previous');
      }
    });

    // Handle window resize events
    const handleResize = () => {
      glideInstance?.update({
        peek: calculatePeek(), // Update peek on resize
        gap: calculateGap(), // Update gap on resize
      });
    };

    // Add resize event listener
    window.addEventListener('resize', handleResize);

    // Cleanup on component unmount
    return () => {
      /**
       * Destroy Glide instance
       */
      glideInstance.destroy();
      window.removeEventListener('resize', handleResize); // Remove resize event listener
    };
  }, [direction]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ArrowDisabler = function (Glide: any, Components: any) {
    return {
      mount() {
        // Only in effect when rewinding is disabled
        if (Glide.settings.rewind) {
          return;
        }
        Glide.on(['mount.after', 'run'], () => {
          // Filter out arrows_control
          for (const controlItem of Components.Controls.items) {
            if (controlItem.className !== 'glide__arrows') {
              continue;
            }

            // Update arrow states
            const leftArrow = controlItem?.querySelector(direction ? '.glide__arrow--right' : '.glide__arrow--left');
            const rightArrow = controlItem?.querySelector(direction ? '.glide__arrow--left' : '.glide__arrow--right');

            if (leftArrow) {
              if (Glide.index === 0) {
                leftArrow.classList.add('is-disabled'); // Disable on first slide
                leftArrow?.classList?.remove('glide__arrow--disabled'); // Ensure old class is removed
              } else {
                leftArrow.classList.remove('is-disabled'); // Enable on other slides
                leftArrow?.classList?.remove('glide__arrow--disabled'); // Ensure old class is removed
              }
            }

            // Set right arrow state
            if (rightArrow) {
              if (Glide.index === Components.Sizes.length - Glide.settings.perView) {
                rightArrow.classList.add('is-disabled'); // Disable on last slide
                rightArrow?.classList?.remove('glide__arrow--disabled'); // Ensure old class is removed
              } else {
                rightArrow.classList.remove('is-disabled'); // Disable on other slides
                rightArrow?.classList?.remove('glide__arrow--disabled'); // Ensure old class is removed
              }
            }
          }
        });
      },
    };
  };

  useEffect(() => {
    const previousButtonEl = previousButton.current;
    const nextButtonEl = nextButton.current;

    slideRefs.current.forEach((el, index) => {
      if (!el) return;
      const slideNumber = index;

      if (nextIndex === 'Next' && slideNumber === activeIndex && nextButtonEl) {
        el.classList.add('slide-in-from-right');
      } else if (nextIndex === 'Previous' && slideNumber === activeIndex && previousButtonEl) {
        el.classList.add('slide-in-from-left');
      } else {
        slideNumber !== activeIndex && el.classList.remove('slide-in-from-left', 'slide-in-from-right');
      }
    });
  }, [activeIndex, nextIndex, direction]);

  return (
    <StyledSignatureCarousel
      data-testid="luxury-SignatureCarousel"
      data-component-name="m-luxury-SignatureCarousel"
      className={clsx(props?.model?.styleclass)}
    >
      <div
        className={clsx(
          'glide',
          props?.model?.styleclass,
          props?.model?.styleclass && regex.test(props?.model?.styleclass) && 'color-class'
        )}
        ref={glideRef}
      >
        <div className="glide__track mb-5" data-glide-el="track">
          <ul className="glide__slides signature-slider">
            {shuffledModel?.map((cardName, index) => (
              <li
                className={clsx('glide__slide', (index + 1) % 2 === 0 && 'image-top')}
                data-slide-number={index}
                key={index}
              >
                <div
                  className={clsx(
                    index === activeIndex ? 'change-img-offset-prev' : '',
                    'slide-desc-container',
                    !direction
                      ? index !== activeIndex &&
                          (index === (activeIndex + 1) % shuffledModel?.length
                            ? 'change-img-offset-prev'
                            : 'change-image-offset')
                      : index !== activeIndex &&
                          (index === (activeIndex + 1) % shuffledModel?.length
                            ? 'change-img-offset'
                            : 'change-image-offset')
                  )}
                >
                  <Image
                    renditions={cardName?.dynamicImage?.renditions}
                    dynamic={cardName?.dynamicImage?.dynamic}
                    defaultImageURL={cardName?.dynamicImage?.assetPath}
                    altText={cardName?.dynamicImage?.altText}
                    loading="eager"
                  />
                  <div
                    className={clsx(
                      'd-flex justify-content-center align-items-center',
                      'px-3 py-4',
                      'color-scheme1',
                      'slide-desc-content',
                      'link-border-color',
                      (index + 1) % 2 === 0 && 'slide-desc-content-position',
                      activeIndex === 0 ? 'slide-in-from-left' : 'slide-in-from-right',
                      index !== activeIndex && (index >= activeIndex + 1 ? 'change-desc-left' : 'change-desc-right')
                    )}
                    ref={el => (slideRefs.current[index] = el)} // Assign ref to each slide description
                    data-slide-number={index}
                  >
                    <div className="w-100">
                      <div className="pb-2 mb-1 mb-md-0 t-overline-normal">{cardName?.eyebrowText}</div>
                      <div className="t-title-s d-flex flex-wrap justify-content-center">
                        <div>{cardName?.location ? cardName?.name + ',\u00A0' : cardName?.name}</div>
                        <div>{cardName?.location}</div>
                      </div>
                      <div
                        className="pt-3 t-font-alt-s"
                        dangerouslySetInnerHTML={{ __html: cardName?.description ?? '' }}
                      ></div>
                      <div>
                        <a
                          href={cardName?.ctaProperties?.linkUrl}
                          className="m-link m-link-s mi-link-animation custom_click_track p-0"
                          target={cardName?.ctaProperties?.openInANewTab ? '_blank' : ''}
                          custom_click_track_value={`Signature Carousel|${cardName?.ctaProperties?.linkText}|internal`}
                          rel="noreferrer"
                        >
                          {cardName?.ctaProperties?.linkText}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="d-flex justify-content-center">
          <div className="carouselControlType2">
            <div className="glide__arrows" data-glide-el="controls">
              <button
                className={clsx(
                  direction ? 'right-arrow glide__arrow--right' : 'left-arrow glide__arrow--left',
                  'custom_click_track'
                )}
                data-glide-dir={direction ? '>' : '<'}
                aria-label={props?.model?.previousLabel}
                data-content={props?.model?.previousLabel}
                ref={previousButton}
                custom_click_track_value={`Signature Carousel|${props?.model?.previousLabel}|internal`}
              >
                <span className={clsx(direction ? 'icon-arrow-right' : 'icon-arrow-left')}></span>
              </button>
            </div>
            <div className="glide-btns" data-glide-el="controls[nav]">
              {Array.from({ length: model?.length }, (_, i) => (
                <button key={i} className="glide__bullet" aria-label={`page-${i}`} data-glide-dir={i}></button>
              ))}
            </div>
            <div className="glide__arrows" data-glide-el="controls">
              <button
                className={clsx(
                  direction ? 'left-arrow glide__arrow--left' : 'right-arrow glide__arrow--right',
                  'custom_click_track'
                )}
                data-glide-dir={direction ? '<' : '>'}
                aria-label={props?.model?.nextLabel}
                ref={nextButton}
                data-content={props?.model?.nextLabel}
                custom_click_track_value={`Signature Carousel|${props?.model?.nextLabel}|internal`}
              >
                <span className={clsx(direction ? 'icon-arrow-left' : 'icon-arrow-right')}></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </StyledSignatureCarousel>
  );
};

export const SignatureCarouselCompConfig = {
  emptyLabel: 'SignatureCarousel',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/signature-carousel/v1/signature-carousel`,
};

//TODO:Lint issue to be fixed
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SignatureCarousel = (props: any) => (
  <EditableComponent config={SignatureCarouselCompConfig} {...props}>
    <SignatureCarouselComp {...props} />
  </EditableComponent>
);
